import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import { arrayLast } from "csi/utility/arrayUtility";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { createSearchRegex, trimmedString } from "csi/utility/stringUtility";
import xml from "csi/xml";
import { DROP_ROOT, DndStore } from "designSystem/dnd/index";
import { DndMultiSelectMap } from "designSystem/dnd/types";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { Dictionary, compact, isEmpty, keyBy, orderBy } from "lodash";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { ChangeEvent, createContext, useContext } from "react";
import { DropTargetMonitor } from "react-dnd";
import { ICustomViewAttribute, ICustomViewAttributeGroup } from "src/c8-platform/type/customView";
import { C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeKey } from "src/c8-platform/type/value";
import { CustomViewAttributeMap } from "../../attributes/attributeKeysService";
import { CustomViewOptionalAttributesMaps, ICustomViewUtil, convertContainerToComponentItem } from "../customViewService";
import { ICustomViewOptions } from "../options/CustomViewOptions";
import CustomViewAttribute from "./CustomViewAttribute";
import CustomViewAttributeContainer from "./CustomViewAttributeContainer";
import CustomViewAttributeGroup from "./CustomViewAttributeGroup";
import CustomViewAttributesLevel from "./CustomViewAttributesLevel";
import { getAttributesToSelect, twoAttributesHaveMatchingGroup, twoAttributesHaveMatchingGroupAndContainer } from "./attributeService";

export interface CustomViewAttributesLevelParams {
	name?: string;
	selected?: boolean;
	specBuilder: SpecBuilder;
}

export type AttributeStoreCreator = (loc: ILocaleProvider, levels: CustomViewAttributesLevelParams[], attributeKeys: ICustomViewAttribute[]) => CustomViewAttributesStore;

export interface CustomViewAttributesOptions {
	/** allow hidden attributes to be added (such as for export views) */
	allowHidden?: boolean;

	/** only allow attributes that support filtering, and also restrict other options */
	filterOnly?: boolean;

	/** list of attributes in view is fixed */
	fixed?: boolean;

	/** indicates if ungrouped attributes are only available at the start of the view (property view) */
	flatAttributesFirst?: boolean;

	/** maximum number of attributes allowed */
	maxNumberOfAttributes?: number;

	/** indicates if view does not support grouping */
	noGroupSupport?: boolean;

	/** indicates if view does not support matrices */
	noMatrixSupport?: boolean;

	/** indicates if hidden / not hidden is supported */
	noVisibilitySupport?: boolean;

	/** return true if attribute should not be shown in attribute picker */
	omitAttributeCallback?: (key: Key) => boolean;

	/** return true if container should not be shown in attribute picker */
	omitContainerCallback?: (extendedPath: ExtendedPath) => boolean;

	/** indicates if paths to abstract classes should not be explored to include derived classes */
	omitDerivedClasses?: boolean;

	/** indicates if image attributes should be omitted from the view */
	omitImages?: boolean;

	/** indicates if Created and Modified attributes should be omitted from the view */
	omitMetadata?: boolean;

	/** indicates if special keys to show referenced images should be available */
	omitReferencedImages?: boolean;

	/** callback to react on type change, used to reset selections in canvas attributes placeholders */
	onChangeLevel?: (index: number) => void;

	/** map of optional attribute maps per extended path */
	optionalAttributesMaps?: CustomViewOptionalAttributesMaps;

	/** list of attributes cannot be empty */
	requireOne?: boolean;

	/** use the name property in place of the Node Name attribute */
	useNamePropInPlaceOfNodeNameAttr?: boolean;
}

export const AllOption = { id: "All", res: "SelectAll" };

export default class CustomViewAttributesStore extends ReactionStore implements ICustomViewUtil<ICustomViewOptions> {
	/** can drop cache */
	canDropCache: Record<string, boolean> = {};

	/** map of all containers keyed by container unique id */
	readonly containerMap: Dictionary<CustomViewAttributeContainer> = {};

	/** selected container */
	@observable.ref selectedContainer: ComponentItem;

	/** map of all matrix group levels keyed by the container unique id */
	readonly matrixLevelMap: Dictionary<CustomViewAttributesLevel> = {};

	/** map of all matrix group containers keyed by the matrix group id */
	readonly matrixSourceContainerMap: Dictionary<CustomViewAttributeContainer> = {};

	/** group whose name is currently being editing */
	@observable.ref editingGroup: CustomViewAttributeGroup;

	/** list of levels */
	readonly levels: CustomViewAttributesLevel[] = [];

	/** list item dnd store */
	attributeDndStore: DndStore<CustomViewAttribute>;

	/** locale provider */
	readonly loc: ILocaleProvider;

	/** options for populating attributes */
	readonly options: CustomViewAttributesOptions;

	/** text used to filter attributes */
	@observable searchText = "";

	/** selected matrix group container for which to show its related containers */
	@observable.ref selectedMatrixContainer?: CustomViewAttributeContainer;

	/** selected attributes/groups in custom view - unnamed groups are used for attributes not appearing in a group */
	readonly selections = observable.array<CustomViewAttributeGroup>([], { deep: false });

	/** index of selected hierarchy level */
	@observable private _selectedLevelIndex = 0;

	/** counter used to assign id to groups that are not from attribute groups */
	#selectionGroupCounter = 0;

	/** return value of attributeKeys custom view option detailing selected attributes in view */
	@computed get attributeKeys(): ICustomViewAttribute[] {
		return this.selections.flatMap(({ attributeKeys }) => attributeKeys);
	}

	/** return Set of attribute IDs inside all groups for fast lookup */
	@computed get flatAttributeKeys(): Set<C8AttributeKey> {
		return new Set(this.selections.flatMap((group) => group.attributes.map((attribute) => attribute.id)));
	}

	/** list of all specs in view */
	@computed get attributeSpecsInView(): Spec[] {
		return this.selections.flatMap((selection) => selection.attributes.map((attribute) => attribute.spec));
	}

	/** return first selection group */
	get defaultSelectionGroup(): CustomViewAttributeGroup {
		return this.selections[0];
	}

	/** return true if path hints at any level */
	get hasContextualPathHints(): boolean {
		return this.levels.some((level) => !!level.specBuilder.defaultPathHints);
	}

	/** get a list of selections as specs */
	get selectionSpecs(): Spec[] {
		return this.selections.flatMap((selection) => selection.attributes.map((attribute) => attribute.spec));
	}

	/** return first flat group */
	get firstFlatGroup(): CustomViewAttributeGroup | undefined {
		let flatGroup: CustomViewAttributeGroup;
		for (const s of this.selections) {
			if (s.flat) {
				flatGroup = s;
				break;
			}
		}
		return flatGroup;
	}

	/** return list of container options */
	get containerOptions(): ComponentItem[] {
		const items = this.selectedLevel.containers.map((container) => convertContainerToComponentItem(container));
		items.unshift(AllOption);

		return items;
	}

	/** return list of flat groups */
	get flatGroups(): CustomViewAttributeGroup[] {
		return this.selections.filter((x) => x.flat);
	}

	/** return true if number of selections is 0 if no selections are required, or 1 if so */
	get hasMinimumNumberOfSelections(): boolean {
		return this.numberOfAttributeSelections === (this.options.requireOne ? 1 : 0);
	}

	/** return value of hiddenKeys custom view option detailing hidden attributes in view */
	@computed get hiddenKeys(): ICustomViewAttribute[] {
		return this.selections.flatMap(({ hiddenKeys }) => hiddenKeys);
	}

	/** is the root selection box grayed out */
	get isRootSelectionBoxGrayedOut(): boolean {
		const dragItemMatrixGroupSpecId = this.attributeDndStore.dragItem?.container?.matrixGroupSpec?.uniqueId; 
		if (dragItemMatrixGroupSpecId) {
			for (const group of this.selections) {
				if (group.spec?.uniqueId === dragItemMatrixGroupSpecId) {
					return true;
				}
			}
		}
	}

	/** list of hierarchy level options - levels with a duplicate type are not displayed */
	@computed get levelOptions(): ComponentItem[] {
		const { levels } = this;
		const options = levels.map(({ duplicateLevel, name }, index) => ({
			id: String(index),
			invalid: duplicateLevel,
			name,
		}));

		return options.filter((option) => !option.invalid);
	}

	/** return first level */
	get mainLevel(): CustomViewAttributesLevel {
		return this.levels[0];
	}

	/** return set of keys for visible matrix groups */
	@computed get matrixFilterKeys(): C8AttributeKey[] {
		return this.selections.flatMap(({ matrixFilterKeys }) => matrixFilterKeys);
	}

	/** number of selected attributes */
	get numberOfAttributeSelections(): number {
		return this.selectionAttributes.length;
	}

	/** values of custom view options from attributes tab */
	@computed get optionValues(): Pick<ICustomViewOptions, "attributeKeys" | "hiddenKeys"> | undefined {
		const { attributeKeys, hiddenKeys, options } = this;

		if (!options.fixed) {
			return {
				attributeKeys,
				hiddenKeys,
			};
		}
	};

	/** return actual text to search spec names by */
	@computed get searchRegex(): RegExp | null {
		return createSearchRegex(this.searchText);
	}

	/** selected hierarchy level */
	get selectedLevel(): CustomViewAttributesLevel {
		const { levels, matrixLevelMap, selectedMatrixContainer, _selectedLevelIndex } = this;
		return selectedMatrixContainer ? matrixLevelMap[selectedMatrixContainer.uniqueId] : levels[_selectedLevelIndex];
	}

	/** selected hierarchy level option */
	get selectedLevelOption(): ComponentItem {
		return this.levelOptions[this._selectedLevelIndex];
	}

	/** return true if there are multiple attributes selected within the list items to be dropped */
	get showAddMultipleAttributes(): boolean {
		const { attributeDndStore: { multiSelectMap } } = this;
		return !isEmpty(multiSelectMap) && !multiSelectMap[Object.keys(multiSelectMap)[0]].selected;
	}

	/** return true if there are multiple attributes selected within the selected attributes */
	get showRemoveMultipleAttributes(): boolean {
		const { attributeDndStore: { multiSelectMap } } = this;
		return !isEmpty(multiSelectMap) && multiSelectMap[Object.keys(multiSelectMap)[0]].selected;
	}

	/** return true if view allows attributes to be grouped */
	get supportsGrouping(): boolean {
		return !this.options.noGroupSupport;
	}

	/** return true if view allows attributes to be hidden */
	get supportsVisibility(): boolean {
		return !this.options.noVisibilitySupport;
	}

	/** return list of all selection attribute */
	@computed get selectionAttributes(): CustomViewAttribute[] {
		return this.selections.flatMap((group) => group.attributes);
	}

	/** return set of selected attribute keys */
	@computed get selectionSet(): Set<C8AttributeKey> {
		const keys = this.selectionAttributes.map((attribute) => attribute.id);
		return new Set<C8AttributeKey>(keys);
	}

	/** return set of selected attribute keys with parent group id */
	@computed get selectionSetWithParent(): Set<C8AttributeKey> {
		const keys = this.selectionAttributes.map(({ idWithParent }) => idWithParent);
		return new Set<C8AttributeKey>(keys);
	}

	/** return true if there is room in view for more attributes */
	canAddNumberOfAttributes = (n: number): boolean => {
		const { numberOfAttributeSelections, options: { maxNumberOfAttributes } } = this;
		return !maxNumberOfAttributes || (numberOfAttributeSelections + n <= maxNumberOfAttributes);
	};

	/** use callback if defined, or return true */
	canShowAttribute = (key: Key): boolean => {
		const { mainLevel, options } = this;
		const { omitAttributeCallback, filterOnly } = options;
		let show = !omitAttributeCallback?.(key);

		if (show && filterOnly) {
			const { specBuilder } = mainLevel;
			const spec = specBuilder.getSpecForKey(key);
			show = spec?.canFilter(true);
		}

		return show;
	};

	/** return attribute for given spec */
	createAttribute = (container: CustomViewAttributeContainer, spec: Spec): CustomViewAttribute => {
		return new CustomViewAttribute(container, spec);
	};

	/** dispose of all container reactions */
	disposeOfContainerReactions = (): void => {
		const { containerMap } = this;
		for (const key in containerMap) {
			const container = containerMap[key];
			container.cleanupReactions();
		}
	};

	/** return the selected group (either attribute group or matrix group) with the given spec */
	findSelectionGroupBySpec = ({ uniqueId }: Spec): CustomViewAttributeGroup | undefined => {
		return this.selections.find(({ spec }) => spec?.uniqueId === uniqueId);
	};

	// #region
	/** -------------------------------------------------- DND --------------------------------------------------------------------------- */

	/** in order to implement caching, create another internal function to check if the drag item can be dropped */
	private canDropInternal = (dragItem: CustomViewAttribute | CustomViewAttributeGroup, targetIndex: number, groupId: string): boolean => {
		const { options: { flatAttributesFirst }, selections } = this;
		const lastGroupIndex = selections.length - 1;
		const lastGroup = selections[lastGroupIndex];
		const firstGroup = selections[0];
		const targetGroup = selections.find((x) => x.id === groupId);
		const dropGroupIsNotDropRoot = groupId !== DROP_ROOT;
		let dragItemIsNotChildOfMatrixGroup = true;
		if (dragItem instanceof CustomViewAttribute) {
			const { spec } = dragItem;
			dragItemIsNotChildOfMatrixGroup = !dragItem.container.matrixGroupSpec;
			if (spec.isMatrix || !dragItemIsNotChildOfMatrixGroup && !dragItem.matchingMatrixGroup && dropGroupIsNotDropRoot) {
				// we've got a matrix attribute and no matching selection containers on our hands, allow all flat
				return targetGroup?.flat;
			} else if (groupId === DROP_ROOT && dragItemIsNotChildOfMatrixGroup) {
				// restrict the user from dropping an attribute in the root group's first and last index if
				// it would get pulled into a flat group that is above or below
				if (targetIndex === 0) {
					// there's a flat group below, only the divider at index 0 of the first flat group should light up
					return !firstGroup?.flat;
				} else if (targetIndex === lastGroupIndex + 1) {
					// there's a flat group above, only the divider at the last index of the last flat group should light up
					return !flatAttributesFirst && !lastGroup.flat;
				} else {
					// inbetween, there must be a non-flat group on top and bottom to drop
					const rootGroupAbove = selections[targetIndex - 1];
					const rootGroupBelow = selections[targetIndex];
					return !flatAttributesFirst && !rootGroupAbove?.flat && !rootGroupBelow?.flat;
				}
			} else if (targetGroup?.isMatrixGroup) {
				return !!(dragItem.container.matrixGroupSpec && (targetGroup.spec.uniqueId === dragItem.container.matrixGroupSpec.uniqueId));
			}
		} else if (dragItem instanceof CustomViewAttributeGroup) {
			// if dragging a group, only allow drop inside of flat groups
			// as an antonym of attribute, dragging groups should not highlight first nor last dividers, rather, the root group should light up
			if (dropGroupIsNotDropRoot) {
				const lastIndexOfTargetGroup = targetGroup.attributes.length;
				return !flatAttributesFirst && !!targetGroup.flat && targetIndex !== 0 && targetIndex !== lastIndexOfTargetGroup;
			} else {
				return !flatAttributesFirst || targetIndex !== 0;
			}
		}
		return dragItemIsNotChildOfMatrixGroup;
	};

	/**
	 * Indicate whether the user is allowed to drop under the target index and group id with a given drag type.
	 */
	canDrop = (dragItem: CustomViewAttribute | CustomViewAttributeGroup, targetIndex: number, groupId: string): boolean => {
		const canDropCacheKey = `${dragItem.id}_${targetIndex}_${groupId}`;
		const { canDropCache, canDropInternal } = this;
		if (!isNullOrUndefined(canDropCache[canDropCacheKey])) {
			return canDropCache[canDropCacheKey];
		}

		const canDrop = canDropInternal(dragItem, targetIndex, groupId);
		canDropCache[canDropCacheKey] = canDrop;
		return canDrop;
	};

	/** handle removal of attributes before drop, return whether it is OK to drop */
	@action private prepareGroupForDrop = (attribute: CustomViewAttribute, monitor: DropTargetMonitor, targetIndex: number, groupId: string): boolean => {
		const { selections } = this;
		const { parentGroup, selected } = attribute;
		const indexOfAttribute = parentGroup.indexOfAttribute(attribute);
		const targetGroup = selections.find((x) => x.id === groupId);
		const isTargetGroupDropRoot = groupId === DROP_ROOT;
		let sourceIndex: number;

		if (indexOfAttribute > -1 && selected) {
			sourceIndex = indexOfAttribute;
			if (
				(sourceIndex === targetIndex || sourceIndex === targetIndex - 1) &&
				((!isTargetGroupDropRoot && (!targetGroup || targetGroup.id === parentGroup.id)))
			) {
				// if the attribute is already in the target group at the same index, then do nothing
				return false;
			}
			// remove the attribute without de-selecting
			parentGroup.removeAttribute(attribute, parentGroup.flat);
		}

		if (isTargetGroupDropRoot) {
			// if the attribute exists in another flat group, remove it
			const [group, index] = this.#getFlatGroupAndIndexWhereAttributeExists(attribute);
			group?.removeAttributeAtIndex(index);
		} else if (targetGroup) {
			if (targetGroup.flat) {
				const [group, index] = this.#getFlatGroupAndIndexWhereAttributeExists(attribute);
				group?.removeAttributeAtIndex(index);
			} else {
				const indexOfAttribute = targetGroup.attributes.map((x) => x.id).indexOf(attribute.id);
				if (indexOfAttribute > -1) {
					// is the attribute already existing in the target group? remove it
					targetGroup.removeAttributeAtIndex(indexOfAttribute);
				}
			}
		} else {
			// no target group found, so create a new one
			const targetGroup = new CustomViewAttributeGroup(this, groupId, [attribute], "", true);
			attribute.parentGroup = targetGroup;
		}

		return true;
	};

	/** handle dropping the attribute from drag */
	@action private dropAttribute = (attribute: CustomViewAttribute, monitor: DropTargetMonitor, targetIndex: number, groupId: string): void => {
		const { selections, supportsGrouping } = this;
		const { matchingMatrixGroup, parentGroup, selected } = attribute;
		const isDropLeftToRight = !selected;
		const indexOfAttribute = parentGroup.indexOfAttribute(attribute);
		const targetGroup = selections.find((x) => x.id === groupId);
		let sourceIndex: number;

		if (indexOfAttribute > -1 && selected) {
			sourceIndex = indexOfAttribute;
		}

		if (groupId === DROP_ROOT) {
			// before creating a new group, there could be a root drop group below or above the target index
			// if that's the case, add it to that group
			const rootGroupAbove = selections[targetIndex - 1];
			const rootGroupBelow = selections[targetIndex];
			if (rootGroupAbove?.flat) {
				attribute = rootGroupAbove.addAttributeAtEnd(attribute);
			} else if (rootGroupBelow?.flat) {
				attribute = rootGroupBelow.addAttributeAtStart(attribute);
			} else {
				// the only case to create a new root group (show borders false = flat true) is if there is no root group above or below
				const id = this.#getSelectionGroupId();
				const targetGroup = new CustomViewAttributeGroup(this, id, [attribute], "", true);
				attribute.parentGroup = targetGroup;
				selections.splice(targetIndex, 0, targetGroup);
			}
		} else {
			// assign new group and move the attribute
			const { matrixGroupSpec } = attribute.container;
			if (!!matrixGroupSpec && !matchingMatrixGroup) {
				// need to create and place the attribute in the group
				const selectionGroup = new CustomViewAttributeGroup(
					this,
					this.#getSelectionGroupId(matrixGroupSpec),
					[],
					matrixGroupSpec.name,
					true,
					matrixGroupSpec,
				);
				attribute = selectionGroup.addAttribute(attribute);
				this.dropGroup(selectionGroup, monitor, targetIndex, groupId);
			} else if (!targetGroup && matchingMatrixGroup) {
				// if no target group, but matching matrix group exists, then add to the matching matrix group
				matchingMatrixGroup.addAttributeAtIndex(attribute, sourceIndex, targetIndex);
				attribute.parentGroup = matchingMatrixGroup;
			} else if (targetGroup) {
				attribute = targetGroup.addAttributeAtIndex(attribute, sourceIndex, targetIndex, parentGroup.id === targetGroup.id);
			} else {
				console.warn("No target group found!", attribute, monitor, targetIndex, groupId);
			}
		}

		// determine where the attribute moved from / to
		const fromGroup = parentGroup;
		const toGroup = attribute.parentGroup;
		const { selectAttribute, unselectAttribute } = attribute.container;
		if (fromGroup !== toGroup) {
			// don't remove unless fromGroup was not flat
			if (fromGroup.spec) {
				unselectAttribute(attribute, fromGroup.spec);
			}
			const selectAttributeInAllContainers = (isDropLeftToRight && (!supportsGrouping || !fromGroup.isAttributeGroup)) || toGroup.isMatrixGroup;
			selectAttribute(attribute, supportsGrouping, toGroup.spec, selectAttributeInAllContainers);
		}

		this.canDropCache = {};
	};

	/** select items until the given item, return true if no extra items can be selected */
	@action shiftSelectItems = (
		item: CustomViewAttribute,
		lastSelectedItem: CustomViewAttribute,
		multiSelectMap: DndMultiSelectMap<CustomViewAttribute>,
		isSelected?: boolean,
	): boolean => {
		// no selection can be made if the last selected item is non-existant
		if (!lastSelectedItem) {
			return false;
		}

		let attributeIdsToSelect: C8AttributeKey[] = [];
		let attributesToSelect: CustomViewAttribute[] = [];
		
		// check if the parent group is selected
		if (isSelected) {
			// the parent group must match
			if (!twoAttributesHaveMatchingGroup(lastSelectedItem, item)) {
				return false;
			}

			const { parentGroup: itemParentGroup } = item;
			const { attributeIds, attributes } = itemParentGroup;
			attributeIdsToSelect = getAttributesToSelect(attributeIds, lastSelectedItem, item);
			attributesToSelect = attributes.filter((x) => attributeIdsToSelect.includes(x.id));
		} else {
			// the container of the last selected item must match the current item
			if (!twoAttributesHaveMatchingGroupAndContainer(lastSelectedItem, item)) {
				return false;
			}

			// the containers match, get all the attributes in-between the last selected item and the current item
			const { attributeIds, attributeMap } = item.container;
			// only get unselected attributes
			attributeIdsToSelect = getAttributesToSelect(
				attributeIds,
				lastSelectedItem,
				item,
			);
			attributesToSelect = Object.values(attributeMap).filter(((x) => !x.selected && attributeIdsToSelect.includes(x.id)));
		}


		attributesToSelect.forEach((attribute) => {
			const { globalId } = attribute;
			multiSelectMap[globalId] = attribute;
		});

		return true;
	};

	/** prepare for drop and if OK, drop */
	@action private prepareGroupAndDropAttribute = (item: CustomViewAttribute, monitor: DropTargetMonitor, targetIndex: number = -1, groupId: string = DROP_ROOT): void => {
		const continueDrop = this.prepareGroupForDrop(item, monitor, targetIndex, groupId);
		if (continueDrop) {
			this.dropAttribute(item, monitor, targetIndex, groupId);
		}
	};

	/** drop group or attribute */
	@action dropItem = (item: CustomViewAttributeGroup | CustomViewAttribute | CustomViewAttribute[], monitor: DropTargetMonitor, targetIndex: number = -1, groupId: string = DROP_ROOT): void => {
		if (targetIndex === -1) {
			return;
		}

		if (monitor.didDrop()) {
			return;
		}

		if (item instanceof CustomViewAttributeGroup) {
			const group: CustomViewAttributeGroup = item;
			this.dropGroup(group, monitor, targetIndex, groupId);
		} else if (item instanceof CustomViewAttribute) {
			const attribute: CustomViewAttribute = item;
			this.prepareGroupAndDropAttribute(attribute, monitor, targetIndex, groupId);
		} else if (Array.isArray(item)) {
			item.forEach((attribute: CustomViewAttribute) => {
				const { parentGroup } = attribute;
				// if it's the same group, the order could change because of the removal of the attribute
				const isDroppingInSameGroup = groupId === parentGroup.id;
				const indexOfAttribute = parentGroup.indexOfAttribute(attribute);
				const isSameGroupAndDroppingAfterSourceIndex = isDroppingInSameGroup && indexOfAttribute < targetIndex;
				const continueDrop = this.prepareGroupForDrop(attribute, monitor, targetIndex, groupId);
				if (continueDrop) {
					// check if attribute got removed above the attribute that's about to be added, subtract one to maintain order
					targetIndex = isSameGroupAndDroppingAfterSourceIndex ? targetIndex - 1 : targetIndex;
					this.dropAttribute(attribute, monitor, targetIndex, groupId);
				}
			});
		}

		this.joinAdjacentFlatGroups();
		this.removeEmptyFlatGroups();
	};

	/** drop group */
	@action private dropGroup = (group: CustomViewAttributeGroup, monitor: DropTargetMonitor, targetIndex: number, groupId: string): void => {
		const { selections } = this;
		const { selected } = group;
		const indexOfGroup = selections.indexOf(group);
		const isTargetGroupDropRoot = groupId === DROP_ROOT;
		let sourceIndex: number;

		if (indexOfGroup > -1 && selected) {
			sourceIndex = indexOfGroup;
			if (
				(isTargetGroupDropRoot && (sourceIndex === targetIndex || sourceIndex === targetIndex - 1))
			) {
				// if the attribute is already in the target group at the same index, then do nothing
				return;
			}
			// remove the group without de-selecting
			selections.remove(group);
		}

		if (groupId === DROP_ROOT) {
			// the only case to create a new root group (show borders false = flat true) is if there is no root group above or below
			selections.splice(sourceIndex < targetIndex ? targetIndex - 1 : targetIndex, 0, group);
		} else {
			const targetGroup = selections.find((x) => x.id === groupId);
			if (targetGroup?.flat) {
				const indexOfTargetGroup = selections.indexOf(targetGroup);
				// split flat group into two groups
				const [newFlatGroupA, newFlatGroupB] = this.#splitFlatGroup(targetGroup, targetIndex);
				selections.splice(indexOfTargetGroup, 0, ...[newFlatGroupA, group, newFlatGroupB]);
				selections.remove(targetGroup);
			}
		}

		this.canDropCache = {};
	};

	/** add attributes to selection list */
	@action addMultiSelectedAttributes = (): void => {
		const { multiSelectItemArray } = this.attributeDndStore;
		const sortedAttributes = orderBy(multiSelectItemArray, ["order"], ["asc"]);
		sortedAttributes.forEach((attribute) => this.addAvailableAttribute(attribute));
	};

	/** remove attributes from selection list */
	@action removeMultiSelectedAttributes = (): void => {
		const { multiSelectItemArray } = this.attributeDndStore;
		multiSelectItemArray.forEach((attribute) => this.removeAvailableAttribute(attribute));
	};

	/** ---------------------------------------------------- DND ------------------------------------------------------------- */
	// #endregion

	/** return container for given key */
	getContainerForKey = (key: Key, checkForMatrix?: boolean): CustomViewAttributeContainer => {
		const { getContainer, matrixSourceContainerMap } = this;
		const { candidateMatrixGroupId, extendedPath, matrix } = key;

		if (matrix) {
			const matrixContainer = matrixSourceContainerMap[matrix];
			if (matrixContainer) {
				this.#createMatrixLevel(matrixContainer);
			}
		}

		// TODO: eventually we may want to support adding ad-hoc containers for paths that exist in the model, but
		// are not specified as preference paths - for now though, just ignore such keys

		// we use the domain path here since a container contains attributes from primary and secondary classes
		let container = getContainer(extendedPath.domainPath, matrix);

		if (!container && checkForMatrix) {
			const matrixContainer = matrixSourceContainerMap[candidateMatrixGroupId];
			if (matrixContainer?.extendedPath.value === key.extendedPathThroughAttribute.value) {
				container = matrixContainer;
			}
		}

		return container;
	};

	/** return container for given path and matrix */
	getContainer = (extendedPath: ExtendedPath, matrixGroupId?: string): CustomViewAttributeContainer | undefined => {
		const containerKey = Key.getPathAndMatrixId(extendedPath, matrixGroupId);
		return this.containerMap[containerKey];
	};

	/** return true if any level starts with given type */
	hasLevelForType = (nodeType: C8NodeType): boolean => {
		return this.levels.some(({ specBuilder }) => specBuilder.defaultSourceType === nodeType);
	};

	/** convert Spec to componentItem in custom view, look for group through attribute store containers */
	convertSpecToComponentItemForCustomView = (spec: Spec): ComponentItem => {
		const { key, name } = spec;
		const container = this.getContainerForKey(key);

		// when the target class is an abstract class, domain type does not exist. We will use the end type if that is the case
		const groupName = container?.name || this.loc.type(key.domainType) || this.loc.type(key.extendedPath.endType);

		return {
			id: spec.uniqueId,
			name,
			group: groupName.toUpperCase(),
		};
	};

	/** add available attribute to selection list */
	@action addAvailableAttribute = (attribute: CustomViewAttribute): void => {
		const { attributeDndStore: { multiSelectMap }, selections, supportsGrouping } = this;
		const { container, globalId, parentGroup } = attribute;
		const { matrixGroupSpec, selectAttribute } = container;
		const { spec } = parentGroup;
		const groupSpec = matrixGroupSpec || spec;

		if (groupSpec && supportsGrouping) {
			// find associated selection group where to add attribute - if not there, then add it;
			// note that attribute groups cannot be represented in a matrix group, so it is first and foremost added to the matrix
			let selectionGroup = this.findSelectionGroupBySpec(groupSpec);

			if (!selectionGroup) {
				selectionGroup = new CustomViewAttributeGroup(
					this,
					this.#getSelectionGroupId(groupSpec),
					[],
					groupSpec.name,
					true,
					groupSpec,
				);

				selections.push(selectionGroup);
			}

			selectionGroup.addAttribute(attribute);
		} else {
			// add attribute to appropriate flat group
			this.#getFlatGroupForAttributes()
				.addAttribute(attribute);
		}

		selectAttribute(attribute, supportsGrouping && !matrixGroupSpec);

		// attribute could be selected for multiselect, remove it if so
		if (multiSelectMap[globalId]) {
			delete multiSelectMap[globalId];
		}
	};

	/** add level for available attributes */
	@action addLevel = (params: CustomViewAttributesLevelParams): void => {
		const { levels, loc } = this;
		const { name, selected, specBuilder } = params;
		const { defaultSourceType, pathHintsId } = specBuilder;
		const resolvedName = name || loc.type(defaultSourceType);
		const levelsByPathHints = keyBy(levels, (level) => level.specBuilder.pathHintsId);
		const duplicateLevel = !!levelsByPathHints[pathHintsId];

		levels.push(new CustomViewAttributesLevel(this, resolvedName, specBuilder, duplicateLevel, !levels.length));

		if (selected) {
			this._selectedLevelIndex = levels.length - 1;
		}
	};

	/** convert view selections into attribute selections */
	@action addSelections = (value?: ICustomViewAttribute[], hiddenKeys?: ICustomViewAttribute[]): void => {
		const { mainLevel: { createAttributeSpec }, options: { useNamePropInPlaceOfNodeNameAttr }, selections } = this;
		const hiddenMap = new CustomViewAttributeMap(hiddenKeys, true);

		const createUnnamedGroup = (): CustomViewAttributeGroup => {
			const group = new CustomViewAttributeGroup(this, this.#getSelectionGroupId(), [], "", true);
			selections.push(group);
			return group;
		};

		// always create default group
		let currentGroup = createUnnamedGroup();

		value?.forEach((groupOrKey) => {
			if (typeof groupOrKey === "string") {
				let keyString: string = groupOrKey;
				if (useNamePropInPlaceOfNodeNameAttr) {
					const key = new Key({ dpkey: groupOrKey });
					if (key.id === xml.ATTR_NAME) {
						keyString = new Key({
							prop: xml.PROP_NAME,
							extendedPath: key.extendedPath.clone(),
						}).toString();
					}
				}
				const a = this.#createSelectedAttributeFromAttributeKey(keyString, hiddenMap);
				if (a) {
					currentGroup ||= createUnnamedGroup();
					currentGroup.addAttribute(a);
				}
			} else {
				const { groupKey, keys, name } = groupOrKey;
				const spec = groupKey ? createAttributeSpec(new Key({ dpkey: groupKey }), { name }) : null;
				const attributes = compact(keys.map((key) => this.#createSelectedAttributeFromAttributeKey(key, hiddenMap, groupOrKey)));

				if (attributes.length) {
					selections.push(new CustomViewAttributeGroup(this, this.#getSelectionGroupId(spec), attributes, name, true, spec));
					currentGroup = undefined;
				}
			}
		});
	};

	/** event handler to start creation of a custom group */
	@action onAddAdHocGroup = (): void => {
		const id = this.#getSelectionGroupId();
		const group = new CustomViewAttributeGroup(this, id, [], "", true);
		this.selections.push(group);
		this.editingGroup = group;
	};

	/** stop editing group and set name; if name is empty and no previous name, then remove */
	@action onBlurEditingGroup = (e: React.FocusEvent<HTMLInputElement>): void => {
		const { editingGroup } = this;
		const { value } = e.target;
		const name = trimmedString(value);

		// TODO: validate group name is not duplicate?

		if (!name && !editingGroup.name) {
			this.selections.remove(editingGroup);
		} else {
			editingGroup.setName(name);
		}

		this.editingGroup = null;
	};

	/** change the selected level */
	@action onChangeLevel = (event: ChangeEvent<HTMLInputElement>): void => {
		this._selectedLevelIndex = Number(event.target.value);
		this.options.onChangeLevel?.(this._selectedLevelIndex);
	};

	@action onChangeSelectedContainer = (event: ChangeEvent<{}>, item: ComponentItem): void => {
		this.selectedContainer = item;
	};
	
	/** reset matrix container and go back to displaying normal set of containers */
	@action onClickMatrixContainerBack = (): void => {
		this.selectedMatrixContainer = null;
	};

	/** cancel editing group */
	@action onEscapeEditingGroup = (): void => {
		const { editingGroup, selections } = this;

		if (!editingGroup.name) {
			selections.remove(editingGroup);
		}

		this.editingGroup = null;
	};

	/** ensure the selected container is expanded when first rendered */
	@action onRenderLevel = (): void => {
		this.selectedLevel.selectedContainer.populate(true);
	};

	/** register container with store - a container with the same id should not be there  */
	registerContainer = (container: CustomViewAttributeContainer): void => {
		const { containerMap, matrixSourceContainerMap } = this;
		const { sourceMatrixGroupSpec, uniqueId } = container;

		const existingContainer = containerMap[uniqueId];
		if (existingContainer) {
			return;
		}

		containerMap[uniqueId] = container;

		if (sourceMatrixGroupSpec) {
			matrixSourceContainerMap[sourceMatrixGroupSpec.matrixGroupId] = container;
		}
	};

	/** remove available attribute from selection list */
	@action removeAvailableAttribute = (attribute: CustomViewAttribute): void => {
		const { attributeDndStore: { multiSelectMap }, supportsGrouping } = this;
		const { container, globalId, id, parentGroup } = attribute;
		const { matrixGroupSpec, unselectAttribute } = container;
		const groupSpec = matrixGroupSpec || parentGroup.spec;

		if (groupSpec && supportsGrouping) {
			const group = this.findSelectionGroupBySpec(groupSpec);
			group.removeAttributeKey(id, true);
		} else {
			this.selections.forEach((group) => {
				group.removeAttributeKey(id, true);
			});
		}

		unselectAttribute(attribute, groupSpec);

		if (multiSelectMap[globalId]) {
			delete multiSelectMap[globalId];
		}
	};

	/** remove selected attribute */
	@action removeSelectedAttribute = (attribute: CustomViewAttribute): void => {
		const { container, parentGroup } = attribute;
		const { isLastGroup, removeAttribute, spec } = parentGroup;

		// remove attribute from group and group too if empty
		removeAttribute(attribute, !isLastGroup);

		// unset selected for corresponding available attribute
		container.unselectAttribute(attribute, spec);
	};

	/** set search text for attribute specs */
	@action setSearchText = (text: string): void => {
		this.searchText = text;
	};

	/** select matrix group container to show its related containers */
	@action showMatrixGroupContainers = (container: CustomViewAttributeContainer): void => {
		this.#createMatrixLevel(container);
		this.selectedMatrixContainer = container;
	};

	/** return id for selection group */
	#getSelectionGroupId = (spec?: Spec): string => {
		return spec?.uniqueId ?? `Group_${++this.#selectionGroupCounter}`;
	};

	/** create level for matrix group if not already done */
	#createMatrixLevel = (container: CustomViewAttributeContainer): void => {
		const { level, name, uniqueId } = container;
		this.matrixLevelMap[uniqueId] ||= new CustomViewAttributesLevel(this, name, level.specBuilder, false, false, container);
	};

	/** return selected attribute for given attribute key (not for matrix keys) */
	#createSelectedAttributeFromAttributeKey = (attributeKey: C8AttributeKey, hiddenMap: CustomViewAttributeMap, parentGroup?: ICustomViewAttributeGroup): CustomViewAttribute => {
		const key = new Key({ dpkey: attributeKey });
		const container = this.getContainerForKey(key);

		// NOTE: we should never get into this situation once the feature is complete, but for now it
		// can happen because we do not yet have support for matrices; if an attribute is invalid,
		// then it should be thrown out when first reading the custom view rather than at this point
		if (!container) {
			console.error(`Container not found for key ${key.toString()}`);
			return null;
		}

		const spec = container.level.createAttributeSpec(key, null, null, true);
		const attribute = new CustomViewAttribute(container, spec, true);

		// attribute is hidden if either it or its parent group is in the hidden map
		attribute.hidden = hiddenMap.has(attributeKey, parentGroup);

		return attribute;
	};

	/** transfer attributes to new group */
	@action transferAllAttributesToGroup = (sourceGroup: CustomViewAttributeGroup, targetGroup: CustomViewAttributeGroup): void => {
		const sourceAttributes = [...sourceGroup.attributes];

		sourceAttributes.forEach((attribute) => {
			sourceGroup.removeAttribute(attribute);
			targetGroup.addAttribute(attribute);
		});

		this.selections.remove(sourceGroup);
	};

	/** return flat group to add next available attribute to */
	#getFlatGroupForAttributes(): CustomViewAttributeGroup {
		const { defaultSelectionGroup, options, selections } = this;
		const { flatAttributesFirst, noGroupSupport } = options;

		if (flatAttributesFirst || noGroupSupport) {
			return defaultSelectionGroup;
		}

		let lastGroup = arrayLast(selections);

		// create new flat group if last one is grouped or if there are no groups
		if (!lastGroup?.flat) {
			lastGroup = new CustomViewAttributeGroup(
				this,
				this.#getSelectionGroupId(),
				[],
				"",
				true,
			);

			selections.push(lastGroup);
		}

		return lastGroup;
	}

	/** split a flat group into two separate groups */
	#splitFlatGroup = (targetGroup: CustomViewAttributeGroup, targetIndex: number): [CustomViewAttributeGroup, CustomViewAttributeGroup] => {
		const newFlatGroupA = new CustomViewAttributeGroup(
			this,
			this.#getSelectionGroupId(),
			[...targetGroup.attributes.slice(0, targetIndex)],
			"",
			true,
		);
		const newFlatGroupB = new CustomViewAttributeGroup(
			this,
			this.#getSelectionGroupId(),
			[...targetGroup.attributes.slice(targetIndex)],
			"",
			true,
		);
		return [newFlatGroupA, newFlatGroupB];
	};

	/** does a specific attribute exist in any flat group */
	#getFlatGroupAndIndexWhereAttributeExists = (attribute: CustomViewAttribute): [CustomViewAttributeGroup, number] => {
		for (const g of this.flatGroups) {
			for (const [i, a] of g.attributes.entries()) {
				if (a.id === attribute.id) {
					return [g, i];
				}
			}
		}
		return [null, null];
	};

	/** join two flat groups at index */
	joinFlatGroupsAtIndex = (index: number): boolean => {
		const { selections } = this;
		const groupAbove = selections[index - 1];
		const groupBelow = selections[index];

		if (groupBelow?.flat && groupAbove?.flat) {
			this.transferAllAttributesToGroup(groupBelow, groupAbove);
			return true;
		}

		return false;
	};

	/** join all adajacent flat groups */
	joinAdjacentFlatGroups = (): void => {
		const { selections } = this;
		for (let i = selections.length - 1; i > 0; i--) {
			this.joinFlatGroupsAtIndex(i);
		}
	};

	/** remove flat groups with no attributes */
	@action removeEmptyFlatGroups = (): void => {
		const { selections } = this;
		for (let i = selections.length - 1; i > 0; i--) {
			const sel = selections[i];
			if (!sel) {
				break;
			}
			if (sel.flat && !sel.attributes.length) {
				selections.remove(sel);
			}
		}
	};

	/** @override */
	@action onChangeOptions = (options: ICustomViewOptions): void => {
		const { addSelections, reevaluateSelections, selections } = this;
		const { attributeKeys, hiddenKeys } = options;

		// completely reset the selections (right side selections) with new keys
		selections.clear();
		addSelections(attributeKeys, hiddenKeys);

		// now that we have re-constructed selection array, reset checkmarks in left side groups
		reevaluateSelections();
	};

	/** reevaluate selections of each group of each container in store based on selection set */
	@action reevaluateSelections = (): void => {
		const { containerMap } = this;
		for (const key in containerMap) {
			const container = containerMap[key];
			container.reevaluateSelections();
		}
	};

	constructor(loc: ILocaleProvider, options?: CustomViewAttributesOptions) {
		super();
		makeObservable(this);
		this.options = options ?? {};
		this.loc = loc;
		this.selectedContainer = AllOption;

		this.trackReaction(reaction(
			() => this.selectedLevel,
			() => {
				this.selectedContainer = AllOption;
			},
		));

		// add reaction for searching custom view attributes
		this.trackReaction(reaction(
			() => [this.searchText, this.selectedLevel],
			() => {
				this.selectedLevel.searchLevel(this.searchText);
			},
		));

		this.trackReaction(reaction(
			() => this.selectedContainer,
			() => {
				this.selectedLevel.collapseAll();
			},
		));
	}
}

export const CustomViewAttributesStoreContext = createContext<CustomViewAttributesStore>(undefined);
export const useCustomViewAttributesStore = (): CustomViewAttributesStore => useContext(CustomViewAttributesStoreContext);